import type { IProgressPromise } from '../interface/promise';

async function anyInOrder<T>(all: (Promise<T> | IProgressPromise<T>)[]): Promise<T> {
    let errorResult: Error | null = null;

    for (const promise of all) {
        /* eslint-disable-next-line no-await-in-loop */
        const result = await promise.catch((promiseError: Error) => promiseError);

        if (result instanceof Error) {
            errorResult = result;
        } else {
            return result;
        }
    }

    throw errorResult ?? new Error(`Failed to run promises ${String(all)}`);
}

/** Sleep promise, will await #ms milliseconds before returning */
const delay = (ms: number) =>
    new Promise<void>(resolve => {
        setTimeout(resolve, ms);
    });

async function retry<T>(
    factory: () => Promise<T> | IProgressPromise<T>,
    delayBetweenRetries = 1000,
    retryCount = 0,
): Promise<T> {
    if (retryCount < 0) {
        throw new Error('Retry count exceeded, cannot continue (<0)');
    }

    const promise = factory();

    let response = await promise.catch((error: Error) => error);

    if (response instanceof Error) {
        if (delayBetweenRetries > 0) {
            await delay(delayBetweenRetries).catch(() => undefined);
        }

        const canRetry = retryCount > 1;

        if (canRetry) {
            response = await retry(factory, delayBetweenRetries, retryCount - 1);
        } else {
            throw response;
        }
    }

    return response;
}

export default Object.freeze({
    anyInOrder,
    delay,
    retry,
});
